import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  AGILE_ORG,
  AGILE_ORG_NAME,
  LABEL_MAPPINGS,
  STAR_VENTURE_NAME,
  AGILE_MENTOR_ROLE_TAG,
  AGILE_ALLOWED_CATEGORY_COMMUNITY
} from '../../../utils/constants';
import { API } from '../../../api';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import ROLES from '../../../utils/roles';
import CircleIcon from '@mui/icons-material/Circle';
import { utcToZonedTime, format } from 'date-fns-tz';
import { useAppSelector } from '../../../redux/hooks';
import { getUserDetails } from '../../../utils/orgName';
import imageCompression from 'browser-image-compression';
import { timezones } from '../../profile-page/timeZones';
import { countries } from '../../profile-page/countryData';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Trophybadge from '../../../assets/images/Trophybadge.svg';
import Trophybadge2 from '../../../assets/images/Trophybadge2.svg';
import ThrophyBadgeGreen from '../../../assets/images/ThrophyBadgeGreen.svg';
import {
  IDisplayRoles,
  MemberRole,
  MemberWordOptions,
  isMemberRole,
  memberWord,
  pickLabel
} from '../../../utils/memberTypeLabels';
import { checkOrfium } from '../../../utils/orfiumFunctions';
import { store } from '../../../redux/store';

/** If we need any think on dev and local server */
export const isDevEnvironment = ['dev.dosen2.io', 'localhost'].includes(window?.location?.hostname);

/** Zoom meeting url */
export const createZoomMeetingUrl = (
  callmeetingId: string,
  callDate: string,
  calltime: string,
  callTimeIntervel: number,
  bookedByUser: any
) => {
  const meetingId = callmeetingId;
  const baseUrl = window.location.origin;
  const callStartTimestamp = new Date(callDate + 'T' + calltime + 'Z').getTime();
  const callStartUnixTime = Math.floor(callStartTimestamp / 1000);
  const currentTimestamp = new Date().getTime();
  const currentcallStartUnixTime = Math.floor(currentTimestamp / 1000);
  const callEndUnixTime = callStartUnixTime + callTimeIntervel * 60;
  /**
   * 1 to join as host, 0 to join as attendee. The first user must join as host to start the session.
   */

  let role = 0;
  if (bookedByUser && bookedByUser?.id === getUserDetails()?.userId) {
    role = 1;
  }

  const meetingLink =
    baseUrl + '/#/video-call/' + meetingId + '/' + role + '/20/' + callStartUnixTime + '/' + callTimeIntervel;
  return meetingLink;
};

export const callEndTime = (callDate: string, calltime: string, callTimeIntervel: number) => {
  const callStartTimestamp = new Date(callDate + 'T' + calltime + 'Z').getTime();
  const callStartUnixTime = Math.floor(callStartTimestamp / 1000);
  const currentTimestamp = new Date().getTime();
  const callEndUnixTime = callStartUnixTime + callTimeIntervel * 60;
  return callEndUnixTime;
};

export const formattedTimeSlot = (callTimeIntervel: number) => {
  let timeSlot = '';
  if (callTimeIntervel === 60) {
    timeSlot = '1 Hour';
  } else if (callTimeIntervel === 120) {
    timeSlot = '2 Hours';
  } else if (callTimeIntervel === 180) {
    timeSlot = '3 Hours';
  } else {
    timeSlot = callTimeIntervel + ' Min';
  }

  return timeSlot;
};

/** Dot design Circle Icon */
export const dotDesign = (
  color: 'primary' | 'secondary' | 'inherit' | 'error' | 'info' | 'success' | 'warning' = 'secondary',
  size: number = 4,
  spacing: number = 4
) => {
  return (
    <CircleIcon
      color={color}
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        mx: `${spacing}px`
      }}
    />
  );
};

/** Bio formatter  */
export const bioFormatter = (string1?: string | null, string2?: string | null, string3?: string | null) => {
  const elements = [];
  let s1 = string1;
  let s2 = string2;
  let s3 = string3;

  if (s3) {
    elements.push(s3);
    if (s1) elements.push(dotDesign());
  }

  if (s1) {
    elements.push(s1);
    if (s2) elements.push(dotDesign());
  }

  if (s2) {
    if (s3 && !s2) elements.push(dotDesign());
    elements.push(s2);
  }

  return <>{elements}</>;
};

export const homepageUrl = (
  orgId: string | null,
  coId: string | null,
  grpId: string | null,
  role: string | null,
  homeUrl: string | null
) => {
  let url = '';
  if (role === ROLES.orgAdmin || role === ROLES.grpAdmin) {
    url = `/app/programdetails/${orgId}/${coId}/${grpId}/overview`;
  } else if (role === ROLES.platAdmin) {
    url = `/app/organizations`;
  } else {
    url = homeUrl ? homeUrl : '';
  }
  return url;
};

/** Meeting color */
export function meetingColor() {
  return [
    {
      value: 'Upcoming', // 'New call request + Upcoming
      title: 'Upcoming meeting',
      color: '#0071A9' // Blue
    },
    {
      value: 'Completed', // Past call
      title: 'Completed meeting',
      color: '#28A745' // Green
    },
    {
      value: 'Incomplete', // If < 2 attendees join the call
      title: 'Incomplete meeting',
      color: '#DC3545' // Red
    },
    {
      value: 'Rejected', // Rejected
      title: 'Rejected meeting',
      color: '#ABB5BE' // Gray
    }
  ];
}

/** Check call type */
export const checkCallType = (
  callStatus: string,
  callDate: string,
  callTime: { toString: () => string },
  callTimeInterval: number,
  rejectStatus: boolean | undefined,
  joinedParticipantsCount: number,
  isBookerJoined: boolean
) => {
  const colors = meetingColor();
  const currentTimestamp = new Date().getTime();
  const currentUnixTime = Math.floor(currentTimestamp / 1000);
  const callStartTimestamp = new Date(callDate + 'T' + callTime + 'Z').getTime();
  const callStartUnixTime = Math.floor(callStartTimestamp / 1000);
  const callEndUnixTime = callStartUnixTime + callTimeInterval * 60;

  let colorObject = null;

  if (rejectStatus === true || callStatus === 'Canceled') {
    colorObject = colors.find((color) => color.value === 'Rejected');
  } else if (callStatus === 'Incomplete') {
    // BE-verified attendance (Gmeet): meeting ended but not everyone attended.
    colorObject = colors.find((color) => color.value === 'Incomplete');
  } else if (callStatus === 'Completed') {
    // BE-verified attendance: trust it over the time/headcount heuristic below.
    colorObject = colors.find((color) => color.value === 'Completed');
  } else if (currentUnixTime > callEndUnixTime && joinedParticipantsCount < 2) {
    colorObject = colors.find((color) => color.value === 'Incomplete');
  } else if (currentUnixTime > callEndUnixTime) {
    if (isBookerJoined) {
      colorObject = colors.find((color) => color.value === 'Completed');
    } else {
      colorObject = colors.find((color) => color.value === 'Incomplete');
    }
  } else if (currentUnixTime + 300 > callStartUnixTime) {
    colorObject = colors.find((color) => color.value === 'Upcoming');
  } else {
    colorObject = colors.find((color) => color.value === 'Upcoming');
  }

  const color_code = colorObject?.color || '';
  const call_flag = colorObject ? colorObject.title : '';
  const title = colorObject ? colorObject.title : '';

  let CallType_data = {
    color_code: color_code,
    call_flag: call_flag,
    title: title,
    reviewFlag: true
  };
  return CallType_data;
};

function getTimeZoneNameCodeByValue(value: string) {
  const timezone = timezones.find((zone: { value: string }) => zone.value === value);
  return timezone ? timezone.timeZoneNameCode : 'UTC';
}

function utcToGivenTz(call_date: string, utc_time: string, timezone: string) {
  const time = new Date(call_date + 'T' + utc_time + 'Z');
  const utc_to_tz = utcToZonedTime(time, timezone);
  const formattedTime = format(utc_to_tz, 'h:mm a');
  return formattedTime;
}

export const timeZoneWiseTime = (call_date: string, call_time: string, user_tz: string) => {
  const tz = getTimeZoneNameCodeByValue(user_tz);
  return utcToGivenTz(call_date, call_time, tz);
};

export const timeZoneAbbreviation = (user_tz: string) => {
  const tz = getTimeZoneNameCodeByValue(user_tz);
  const currentTimezoneAbbreviation = moment.tz(tz).format('z');
  return currentTimezoneAbbreviation;
};

export const timeZonWiseDateTime = (call_date: string, utc_time: string, timezone: string) => {
  const tz = getTimeZoneNameCodeByValue(timezone);
  const time = new Date(call_date + 'T' + utc_time + 'Z');
  const utc_to_tz = utcToZonedTime(time, tz);
  const formattedTime = new Date(utc_to_tz);
  return formattedTime;
};

export const formatMMDDYYYY = (call_date: string, utc_time: string, timezone: string) => {
  const tz = getTimeZoneNameCodeByValue(timezone);
  const time = new Date(call_date + 'T' + utc_time + 'Z');
  const utc_to_tz = utcToZonedTime(time, tz);
  const formattedTime = format(utc_to_tz, 'MM/dd/yyyy');
  return formattedTime;
};

const countOccurrences = (notificationArray: any, threadsArray: any) => {
  let msgCount = 0;
  notificationArray.forEach((notification: any) => {
    const { threadId } = notification;
    if (threadsArray.some((thread: any) => thread.id === threadId)) {
      msgCount = (msgCount || 0) + 1;
    }
  });
  return msgCount;
};

export const notificationCount = (acsArray: any, notificationArray: any) => {
  const counts = {
    personal: countOccurrences(notificationArray, acsArray?.personalThreadsArr),
    group: countOccurrences(notificationArray, acsArray?.groupThreads),
    total: 0
  };
  counts.total = counts.personal + counts.group;
  return counts;
};

export const stringToLower = (givenString: string) => {
  let formatted_string = givenString.toLowerCase();
  return formatted_string;
};

// Similarly we can make validation functions for other Organizations also//
export const IsAgileExecutive = () => {
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const [searchParams] = useSearchParams();
  const orgName = searchParams.get('org') || '';
  const role = localStorage.getItem('role') || '';
  const storedOrgName = role !== ROLES.platAdmin ? oid : orgName || '';
  if (storedOrgName === AGILE_ORG_NAME || storedOrgName === AGILE_ORG) {
    return true;
  }
  return false;
};

export const IsStarVenture = () => {
  const oid = useAppSelector((state) => state.orgDetails.data?.oid);
  const mainGroupName = useAppSelector((state) => state.allGroups.data?.mainGroupName);
  const allGroups = useAppSelector((state) => state.allGroups);
  const [searchParams] = useSearchParams();
  const orgName = searchParams.get('org') || '';
  const role = localStorage.getItem('role') || '';
  const groupRoleList = useAppSelector((state) => state?.groupRoleList);

  const includesStarVenture = groupRoleList?.data?.some((item) =>
    item?.name?.toLowerCase().includes(STAR_VENTURE_NAME)
  );

  const groupList = useAppSelector((state) => state.groupRoleList);

  const getOnlyStarVenture = groupList?.data?.find(
    (each) => each?.name?.toLowerCase() === STAR_VENTURE_NAME || each?.name === STAR_VENTURE_NAME
  );

  const hasStarVentureInGroups = allGroups?.data?.groups?.some(
    (group: any) => group?.name?.toLowerCase() === STAR_VENTURE_NAME || group?.name === STAR_VENTURE_NAME
  );

  const breadcrumbs: any = useAppSelector((state) => state.breadCrumbs.breadcrumbs);

  const updatedBreadcrumbs = breadcrumbs.map((breadcrumb: any, index: any) => {
    if (breadcrumb?.name?.toLowerCase() === STAR_VENTURE_NAME && index < breadcrumbs.length - 1) {
      return { ...breadcrumb, name: STAR_VENTURE_NAME };
    }
    return breadcrumb;
  });

  const containsStarVenture = updatedBreadcrumbs.some(
    (breadcrumb: any, index: any) =>
      breadcrumb.name?.toLowerCase() === STAR_VENTURE_NAME && index < updatedBreadcrumbs.length - 1
  );

  const storedOrgName =
    role !== ROLES.platAdmin
      ? getOnlyStarVenture?.name?.toLowerCase().replace(/[^a-z0-9]/g, '') || ''
      : mainGroupName?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
  if (
    (role !== ROLES.platAdmin && hasStarVentureInGroups) ||
    storedOrgName === STAR_VENTURE_NAME ||
    allGroups?.data?.mainGroupName?.toLowerCase() === STAR_VENTURE_NAME ||
    containsStarVenture ||
    includesStarVenture
  ) {
    return true;
  }

  return false;
};

export const roleNameFormatter = (
  roleName: string | undefined,
  formattedMentorName: string,
  formattedMenteeName: string
) => {
  if (roleName) {
    let formattedRoleName = roleName;
    if (roleName === 'Mentor' || roleName === 'mentor') {
      formattedRoleName = formattedMentorName;
    } else if (roleName === 'Mentee' || roleName === 'mentee') {
      formattedRoleName = formattedMenteeName;
    }
    return formattedRoleName;
  } else {
    return '';
  }
};

export function blobToFile(blob: BlobPart, fileName: string, fileType: any) {
  const options: any = {
    type: fileType
  };
  const file = new File([blob], fileName, options);
  return file;
}

export const handleImageCompression = async (imageFile: any) => {
  const options = {
    maxSizeMB: 1,
    // maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  try {
    const compressedFile = await imageCompression(imageFile, options);
    const blobToImageFile = blobToFile(compressedFile, imageFile?.name, imageFile?.type);
    return blobToImageFile;
  } catch (error) {}
};

export const roundOfNumber = (value: any) => {
  let percent = value;
  if (typeof value !== 'number') {
    // Only round of numbers
    return percent;
  }
  percent = Math.round(percent);
  return percent;
};

export const imageSizeLimit = () => {
  const imageSize = {
    limitValue: 10 * 1024 * 1024,
    limitText: '10MB'
  };
  return imageSize;
};

/** Number format with commas ',' */
export const formatNumberWithCommas = (num?: number) => {
  return num?.toLocaleString();
};

/** Number format with everything. EG, DecimalPlaces, commas   */
export function formatNumber(number?: number, decimalPlaces: number = 0) {
  if (number) {
    const factor = Math.pow(10, decimalPlaces);
    const roundedNumber = Math.round(number * factor) / factor;

    return roundedNumber % 1 === 0
      ? roundedNumber.toLocaleString(undefined, { minimumFractionDigits: 0 })
      : roundedNumber.toLocaleString(undefined, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces
        });
  } else {
    return 0;
  }
}

export function showUpvoteIcon(userDetails: any) {
  // userRole 2 is for orgAdmin
  if (userDetails?.mentors?.length > 0 || userDetails?.admins?.length > 0 || userDetails?.userRole === 2) {
    return true;
  }
  return false;
}

export function categoryLabelFormatter(label: any) {
  if (!IsAgileExecutive) {
    return label;
  }
  const mappedLabel = LABEL_MAPPINGS.find((mapping) => mapping.original === label);
  return mappedLabel ? mappedLabel.formatted : label;
}

export function unixToHumanReadable(timestamp: number) {
  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  const diff = currentTime - timestamp;

  if (diff < 60) {
    return `${diff} Seconds ago`;
  } else if (diff < 3600) {
    const minutes = Math.floor(diff / 60);
    return `${minutes} ${minutes === 1 ? 'Min' : 'Mins'} ago`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} ${hours === 1 ? 'Hour' : 'Hours'} ago`;
  } else {
    const days = Math.floor(diff / 86400);
    return `${days} ${days === 1 ? 'Day' : 'Days'} ago`;
  }
}

export function formattedSuffixedDate(date: string) {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const [year, monthStr, dayStr] = date.split('-');
  const month = monthNames[parseInt(monthStr, 10) - 1];
  const day = parseInt(dayStr, 10);

  const shortYear = year.slice(-2);

  let ordinalSuffix = 'th';
  if (day === 1 || day === 21 || day === 31) {
    ordinalSuffix = 'st';
  } else if (day === 2 || day === 22) {
    ordinalSuffix = 'nd';
  } else if (day === 3 || day === 23) {
    ordinalSuffix = 'rd';
  }

  // return `${day}${ordinalSuffix} ${month}, '${shortYear}`;
  return ` ${month} '${shortYear}`;
}

export function formattedCategory(cat: string) {
  if (!IsAgileExecutive) {
    // for other than agile just return the original value
    return cat;
  }
  let formattedCat = AGILE_ALLOWED_CATEGORY_COMMUNITY.includes(cat) ? AGILE_MENTOR_ROLE_TAG : cat;
  return formattedCat;
}

/** Character Count for input field */
export function inputCharacterCount(textValue: any, maxCharacterCount: number) {
  const data = {
    textValueLength: 0 + textValue.length,
    maxCharacterCount: maxCharacterCount
  };
  return data;
}

/** Character Count limit Warning */
export const showCharacterCountWarning = (value: string, maxCharacterCount: number) => {
  toast.dismiss();
  if (value.length === maxCharacterCount) {
    toast.warning(`Character limit of ${maxCharacterCount} characters exceeded`);
  }
};

/** Capitalize First letter */
export function capitalizeFirstLetter(string: any) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Roles Array
 */
export function rolesArray() {
  const roles = ['Mentee', 'Mentor', 'Admin'];
  return roles;
}

/**
 * customer Category Array
 */
export function customerCategoryArray() {
  const customerCategory = ['Business Owner', 'Entrepreneur', 'Consultant', 'Program Friend'];
  return customerCategory;
}

/**
 * Content type Array
 */
export function contentTypeArray() {
  const contentType = ['Audio', 'Video', 'Text'];
  return contentType;
}

const bankIndustries = [
  { label: 'Advertising', name: 'advertising' },
  { label: 'Agritech', name: 'agritech' },
  { label: 'Artificial Intelligence (AI)', name: 'artificialIntelligenceAi' },
  { label: 'Machine Learning (ML)', name: 'machineLearningMl' },
  { label: 'CleanTech and Renewable Energy', name: 'cleanTechAndRenewableEnergy' },
  { label: 'Cybersecurity', name: 'cybersecurity' },
  { label: 'EdTech', name: 'edTech' },
  { label: 'E-commerce and Retail', name: 'eCommerceAndRetail' },
  { label: 'Fintech', name: 'fintech' },
  { label: 'FoodTech', name: 'foodTech' },
  { label: 'HealthTech', name: 'healthTech' },
  { label: 'HRTech', name: 'hrTech' },
  { label: 'InsurTech', name: 'insurTech' },
  { label: 'Internet of Things (IoT)', name: 'internetOfThingsIoT' },
  { label: 'LegalTech', name: 'legalTech' },
  { label: 'Marketing', name: 'marketing' },
  { label: 'Real Estate and PropTech', name: 'realEstateAndPropTech' },
  { label: 'Supply Chain and Logistics', name: 'supplyChainAndLogistics' },
  { label: 'Sustainability', name: 'sustainability' },
  { label: 'Transportation and Mobility', name: 'transportationAndMobility' },
  { label: 'Travel and Hospitality', name: 'travelAndHospitality' }
];

export function industryArray(isNotDevBank = false) {
  const industry = [
    {
      name: 'healthcare',
      label: 'Healthcare'
    },
    {
      name: 'technology',
      label: 'Technology'
    },

    {
      name: 'finance',
      label: 'Finance'
    },

    {
      name: 'manufacturing',
      label: 'Manufacturing'
    },

    {
      name: 'retail',
      label: 'Retail'
    },

    {
      name: 'energy',
      label: 'Energy'
    },

    {
      name: 'construction',
      label: 'Construction'
    },

    /* 8 */
    {
      name: 'telecommunications',
      label: 'Telecommunications'
    },

    /* 9 */
    {
      name: 'agriculture',
      label: 'Agriculture'
    },

    /* 10 */
    {
      name: 'transportation',
      label: 'Transportation'
    },

    /* 11 */
    {
      name: 'education',
      label: 'Education'
    },

    /* 12 */
    {
      name: 'hospitality',
      label: 'Hospitality'
    },

    /* 13 */
    {
      name: 'realEstate',
      label: 'Real Estate'
    },

    /* 14 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cb9"),
      name: 'mediaAndEntertainment',
      label: 'Media and Entertainment'
    },

    /* 15 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cba"),
      name: 'automotive',
      label: 'Automotive'
    },

    /* 16 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cbb"),
      name: 'aerospaceAndDefense',
      label: 'Aerospace and Defense'
    },

    /* 17 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cbc"),
      name: 'pharmaceutical',
      label: 'Pharmaceutical'
    },

    /* 18 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cbd"),
      name: 'foodAndBeverage',
      label: 'Food and Beverage'
    },

    /* 19 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cbe"),
      name: 'chemical',
      label: 'Chemical'
    },

    /* 20 */
    {
      // "_id" : ObjectId("6581dca730846f0d45970cbf"),
      name: 'environmentalServices',
      label: 'Environmental Services'
    }
  ];
  if (!isNotDevBank) {
    return [...industry, ...bankIndustries];
  }
  return industry;
}

/**
 * Currency Array
 */
export function currencyArray() {
  const currencies = [
    { value: '$', symbol: '$' },
    { value: '€', symbol: '€' },
    { value: '£', symbol: '£' }
  ];
  return currencies;
}

/**
 * optionalFields Array
 */

const bankTopics = [
  { label: 'Product', name: 'product' },
  { label: 'Partnerships', name: 'partnerships' },
  { label: 'Growth: B2B Sales', name: 'growthB2bSales' },
  { label: 'Growth: B2C Sales', name: 'growthB2cSales' },
  { label: 'Growth: Conversion Rate', name: 'growthConversionRate' },
  { label: 'Growth: Customer Success', name: 'growthCustomerSuccess' },
  { label: 'Growth: Emailing', name: 'growthEmailing' },
  { label: 'Growth: Offline Advertising', name: 'growthOfflineAdvertising' },
  { label: 'Growth: SEO/SEM', name: 'growthSeoSem' },
  { label: 'Revenue', name: 'revenue' },
  { label: 'Legal: Data Privacy/Security', name: 'legalDataPrivacySecurity' },
  { label: 'Legal: Intellectual Property/Protection', name: 'legalIntellectualPropertyProtection' },
  { label: 'HR: Leadership', name: 'hrLeadership' },
  { label: 'HR: Objectives (OKRs)/KPIs/Goals', name: 'hrObjectivesOkrsKpisGoals' },
  { label: 'HR: Recruitment', name: 'hrRecruitment' },
  { label: 'HR: Culture', name: 'hrCulture' },
  { label: 'Operations: Quality Control', name: 'operationsQualityControl' },
  { label: 'Operations: Supply Chain & Logistics', name: 'operationsSupplyChainLogistics' },
  { label: 'Operations: Cost Reduction & Efficiency', name: 'operationsCostReductionEfficiency' },
  { label: 'Customer Support', name: 'customerSupport' },
  { label: 'Sustainability', name: 'sustainability' },
  { label: 'Strategy: Business Model/Pricing', name: 'strategyBusinessModelPricing' },
  { label: 'Strategy: International Expansion', name: 'strategyInternationalExpansion' },
  { label: 'Strategy: Market Analysis/Positioning', name: 'strategyMarketAnalysisPositioning' },
  { label: 'Tech: UI/UX', name: 'techUiUx' },
  { label: 'Tech: AI Development', name: 'techAiDevelopment' },
  { label: 'Tech: Front & Backend Technology', name: 'techFrontBackendTechnology' }
];

const fpnTopics = [
  { label: 'Sleep Optimization', name: 'Sleep optimization' },
  { label: 'Detox Protocols', name: 'Detox protocols' },
  { label: 'Hormone Balance', name: 'Hormone balance' },
  { label: 'Anti-Aging', name: 'Anti-aging' },
  { label: 'Weight Management', name: 'Weight management' },
  { label: 'Stress Management', name: 'Stress management' },
  { label: 'Chronic Inflammation', name: 'Chronic inflammation' },
  { label: 'Adrenal Support', name: 'Adrenal support' },
  { label: 'Cardiovascular Health', name: 'Cardiovascular health' },
  { label: 'Autoimmune Conditions', name: 'Autoimmune conditions' },
  { label: 'Thyroid Health', name: 'Thyroid health' },
  { label: 'Blood Sugar Regulation', name: 'Blood sugar regulation' },
  { label: 'Peptide Therapy', name: 'Peptide therapy' },
  { label: 'Joint and Musculoskeletal Health', name: 'Joint and musculoskeletal health' },
  { label: 'Pain Management', name: 'Pain management' },
  { label: 'Integrative Oncology', name: 'Integrative oncology' },
  { label: 'Integrative Cardiology', name: 'Integrative cardiology' },
  { label: 'Functional Neurology', name: 'Functional neurology' },
  { label: 'Mental Health and Emotional Wellness', name: 'Mental health and emotional wellness' },
  { label: 'IV Therapy', name: 'Iv therapy' },
  { label: 'Fertility', name: 'Fertility' },
  { label: 'Testosterone Optimization', name: 'Testosterone optimization' },
  { label: 'Pediatrics in Functional Medicine', name: 'Pediatrics in functional medicine' },
  { label: 'Allergies and Food Sensitivities', name: 'Allergies and food sensitivities' },
  { label: 'Ayurvedic Medicine', name: 'Ayurvedic medicine' },
  { label: 'PTSD Recovery', name: 'Ptsd recovery' },
  { label: 'Sports Medicine and Performance Optimization', name: 'Sports medicine and performance optimization' },
  { label: 'Bio Magnetic Field Therapy', name: 'Bio magnetic field therapy' },
  { label: 'Stem Cell Therapy', name: 'Stem cell therapy' },
  { label: 'Exosome Therapy', name: 'Exosome therapy' },
  { label: 'Mitochondrial testing', name: 'Mitochondrial testing' },
  { label: '401K', name: '401k' },
  { label: 'Group health insurance', name: 'Group health insurance' },
  { label: 'Ozone therapy', name: 'Ozone therapy' },
  { label: 'CBD', name: 'Cbd' },
  { label: 'Stress Reduction Techniques-Mindfulness', name: 'Stress reduction techniques-mindfulness' },
  { label: 'Stress Reduction Techniques-Meditation', name: 'Stress reduction techniques-meditation' },
  { label: 'Energy Medicine-PEMF', name: 'Energy medicine-pemf' },
  { label: 'Energy Medicine-Red Light Therapy', name: 'Energy medicine-red light therapy' },
  { label: 'Genetics', name: 'Genetics' },
  { label: 'Genetics Testing', name: 'Genetics testing' },
  { label: 'Environmental Toxicology', name: 'Environmental toxicology' },
  { label: 'Mold', name: 'Mold' },
  { label: 'Brain Health', name: 'Brain health' },
  { label: 'Cognitive Function', name: 'Cognitive function' },
  { label: 'Gut Health', name: 'Gut health' },
  { label: 'Microbiome', name: 'Microbiome' },
  { label: 'Regenerative Medicine', name: 'Regenerative medicine' },
  { label: 'Biologics', name: 'Biologics' },
  { label: 'Affinity Plan Management', name: 'Affinity plan management' },
  { label: 'Employee Retention Tool', name: 'Employee retention tool' },
  { label: 'Benefits Provider', name: 'Benefits provider' },
  { label: 'Holistic Employee Wellness', name: 'Holistic employee wellness' },
  { label: 'Insurance', name: 'Insurance' },
  { label: 'Employee Wellbeing', name: 'Employee wellbeing' },
  { label: 'Cyber Insurance', name: 'Cyber insurance' },
  { label: 'Wealth Management', name: 'Wealth management' },
  { label: 'Financial Strategies', name: 'Financial strategies' },
  { label: 'Retirement Benefits', name: 'Retirement benefits' },
  { label: 'Retirement Planning', name: 'Retirement planning' },
  { label: 'Financial Wellness', name: 'Financial wellness' },
  { label: 'PEP', name: 'Pep' },
  { label: 'Multi-employer Retirement Plans', name: 'Multi-employer retirement plans' },
  { label: 'Exit Strategies', name: 'Exit strategies' },
  { label: 'Estate Planning Strategies', name: 'Estate planning strategies' },
  { label: 'Tax Deferral Strategies', name: 'Tax deferral strategies' },
  { label: 'Financial Performance', name: 'Financial performance' },
  { label: 'Business Valuation', name: 'Business valuation' },
  { label: 'FPN Network', name: 'Fpn network' },
  { label: 'Employee Benefits', name: 'Employee benefits' },
  { label: 'Marketing', name: 'Marketing' },
  { label: 'DNA Testing', name: 'Dna testing' },
  { label: 'Peak Health 247 Marketplace', name: 'Peak health 247 marketplace' },
  { label: 'How-to videos', name: 'How-to videos' },
  { label: 'Treatment Protocols', name: 'Treatment protocols' },
  { label: 'Case Study', name: 'Case study' },
  { label: 'Network Support', name: 'Network support' },
  { label: 'Advertising', name: 'Advertising' },
  { label: 'Digital Marketing', name: 'Digital marketing' },
  { label: 'Search Engine Marketing', name: 'Search engine marketing' },
  { label: 'New Patient Acquisition', name: 'New patient acquisition' },
  { label: 'HIPAA Compliance', name: 'Hipaa compliance' },
  { label: 'Targeted Marketing', name: 'Targeted marketing' },
  { label: 'Geotargeting', name: 'Geotargeting' },
  { label: 'Email Marketing', name: 'Email marketing' },
  { label: 'Behavioral Targeting', name: 'Behavioral targeting' }
];

export function topicArray(isNotDevBank = false) {
  const orgId = store.getState().orgDetails.data?.oid;
  const isFPN = orgId === 'functionalprovidersnetwork';
  const topics = [
    {
      label: 'Accounting',
      name: 'accounting'
    },
    {
      label: 'Career',
      name: 'career'
    },
    {
      label: 'Construction',
      name: 'construction'
    },
    {
      label: 'Data Analysis',
      name: 'dataAnalysis'
    },
    {
      label: 'Economics',
      name: 'economics'
    },
    {
      label: 'Education',
      name: 'education'
    },
    {
      label: 'Engineering',
      name: 'engineering'
    },
    {
      label: 'Entrepreneurship',
      name: 'entrepreneurship'
    },
    {
      label: 'Finance',
      name: 'finance'
    },
    {
      label: 'FYE',
      name: 'fye'
    },
    {
      label: 'History',
      name: 'history'
    },
    {
      label: 'Human Resources',
      name: 'humanResources'
    },
    {
      label: 'Law',
      name: 'law'
    },
    {
      label: 'Logistics',
      name: 'logistics'
    },
    {
      label: 'Marketing',
      name: 'marketing'
    },
    {
      label: 'Math',
      name: 'math'
    },
    {
      label: 'Medicine',
      name: 'medicine'
    },
    {
      label: 'Music',
      name: 'music'
    },
    {
      label: 'Onboarding',
      name: 'onboarding'
    },
    {
      label: 'Philosophy',
      name: 'philosophy'
    },
    {
      label: 'Politics',
      name: 'politics'
    },
    {
      label: 'Sales',
      name: 'sales'
    },
    {
      label: 'Strategy',
      name: 'strategy'
    },
    {
      label: 'Technology',
      name: 'technology'
    },

    // new
    {
      label: 'Communications',
      name: 'communications'
    },
    {
      label: 'Corporate Social Responsibility',
      name: 'corporateSocialResponsibility'
    },
    {
      label: 'Mindset',
      name: 'mindset'
    },
    {
      label: 'Fundraising',
      name: 'fundraising'
    },
    {
      label: 'Governance',
      name: 'governance'
    },
    {
      label: 'Investor Readiness',
      name: 'investorReadiness'
    },
    {
      label: 'Lobbying/Advocacy',
      name: 'lobbyingAdvocacy'
    },
    {
      label: 'Storytelling',
      name: 'storytelling'
    }
  ];
  // condition is correct, this will be useful while mapping bank and non bank topics
  if (!isNotDevBank) {
    return [...topics, ...bankTopics];
  }

  if (isFPN) {
    return [...topics, ...fpnTopics];
  }

  return topics;
}

/**
 * community Fields Array
 */
export function communityFieldsArray() {
  const communityFieldsArray = [
    {
      label: 'Mentors',
      name: 'mentors'
    },
    {
      label: 'Mentees',
      name: 'mentees'
    },
    {
      label: 'Administrators',
      name: 'admins'
    }
  ];
  return communityFieldsArray;
}

// The words an org can pick for each member type (Settings > Members), plus the role codes themselves.
const MENTEE_WORDS = ['mentee', 'learner', 'client', 'employee', 'student'];
const MENTOR_WORDS = ['mentor', 'coach', 'consultant', 'manager', 'buddy', 'faculty'];

/** 'mentee', 'Mentees', 'client', 'Coaches', 'Admin' → the member type it names; any other word → undefined. */
const toMemberRole = (word: string | undefined): MemberRole | undefined => {
  const key = (word || '')
    .trim()
    .toLowerCase()
    .replace(/ies$/, 'y')
    .replace(/(ch|sh|x)es$/, '$1')
    .replace(/s$/, '');
  if (key === 'admin') return 'admin';
  if (MENTEE_WORDS.includes(key)) return 'mentee';
  if (MENTOR_WORDS.includes(key)) return 'mentor';
  return undefined;
};

/** The form a caller's word asks for: 'Mentees' → plural, 'mentor' → lowercase, 'Mentee' → capitalised singular. */
const formOf = (targetedWord: string): MemberWordOptions => ({
  plural: /[^s]s$/i.test(targetedWord.trim()),
  lower: /^[a-z]/.test(targetedWord.trim())
});

const selectOrgDisplayRoles = (state: any): IDisplayRoles | undefined => state?.orgDetails?.data?.displayRoles;

/**
 * The org's word (Settings > Members) for a member type, in the form of `targetedWord`:
 * GroupBasedRoleFormatter('mentee', 'Mentees') → "Employees" when the org calls its learners "employee".
 */
export const GroupBasedRoleFormatter = (roleType: string, targetedWord: string) => {
  const orgDisplayRoles = useAppSelector(selectOrgDisplayRoles);
  return formatRoleFromDisplayRoles(roleType, targetedWord, undefined, orgDisplayRoles);
};

/**
 * The word for a member type (`roleType`: 'mentee' | 'mentor' | 'admin', or an org word such as 'client' or
 * 'coach'), in the form of `targetedWord` ('Mentee', 'mentees', 'Mentors', ...). The org's word wins, also inside a
 * program; `displayRoles` (a program's copy) only fills in a type the org has no word for. Outside a component the
 * org's words are read from the store. A `roleType` that names no member type returns `targetedWord` as given.
 */
export const formatRoleFromDisplayRoles = (
  roleType: string,
  targetedWord: string,
  displayRoles?: IDisplayRoles | null,
  orgDisplayRoles: IDisplayRoles | undefined = selectOrgDisplayRoles(store.getState())
) => {
  const role = toMemberRole(roleType);
  if (!role) return targetedWord;
  const words = pickLabel(orgDisplayRoles?.[role], '') ? orgDisplayRoles : displayRoles ?? undefined;
  return memberWord(role, words, formOf(targetedWord));
};

/** A column heading ('Mentors', 'Mentees', 'Admin') in the org's words; other headings come back as given. */
export const formatCapRoleFromDisplayRoles = (roleType: string, displayRoles: any) =>
  formatRoleFromDisplayRoles(roleType, roleType, displayRoles);

/**
 * The label for a rolesArray() option. The options ('Mentee', 'Mentor', 'Admin') are the values the filters compare
 * and the invite sends, so they stay as they are; show this instead: the org's word for that member type. Anything
 * that is not one of the three codes (a category such as 'Consultant', say) comes back as given.
 */
export const useRoleOptionLabel = () => {
  const orgDisplayRoles = useAppSelector(selectOrgDisplayRoles);
  return (option: string) =>
    isMemberRole(option) ? formatRoleFromDisplayRoles(option, option, undefined, orgDisplayRoles) : option;
};

/** Get Goal Progress show Or Not show */
export function getGoalProgressShowOrNot(goalValue: any) {
  const showGoalProgress = goalValue;
  return showGoalProgress;
}

export function getAverage(numerator: any, denominator: any) {
  let avgValue: any = 0;
  if (denominator && denominator !== 0 && numerator && numerator !== 0) {
    avgValue = formatNumber(numerator / denominator);
  }
  return avgValue;
}

export function getFirstName(name: any) {
  let firstName = '';
  if (name.trim() !== '') {
    // Split the full name by space
    const parts = name.split(' ');
    if (parts.length > 0) {
      firstName = parts[0];
    }
  }
  return firstName;
}

export async function updateInsightDataByArticleId(orgId: any, articleId: any, articleLocation: any, grpId?: string) {
  return await API.updateInsightByArticleId({
    orgId: orgId,
    articleId: articleId,
    isContentSettings: false,
    articleLocation: articleLocation ? articleLocation : '',
    grpId: grpId || undefined
  });
}

/** For loading with set time */
export function showMyLoader(time = 1500) {
  const [loader, setLoader] = useState(true);

  // setTimeout(function() {
  //   setloader(false);
  // }, time);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoader(false);
    }, time);

    return () => clearTimeout(timeoutId);
  }, [time]);

  return loader;
}

/** Handle link clipboard */
export const handleCopyToClipboard = (value: any, successMessage: any, errorMessage?: any) => {
  toast.dismiss();
  navigator.clipboard
    .writeText(value)
    .then(() => {
      toast.success(successMessage);
    })
    .catch((error) => {
      toast.error(errorMessage || 'The link could not be copied. Try again.');
    });
};

/** Toast warning  */
export const toastWarning = (warningMessage?: any) => {
  toast.dismiss();
  toast.warning(warningMessage || 'Warning message!');
};

/** Toast error  */
export const toastError = (errorMessage?: any) => {
  toast.dismiss();
  toast.error(errorMessage || 'Warning message!');
};

/** Lock icon */
export const lockIcon = ({
  size = '13px',
  color = 'white',
  cursor = 'default',
  title
}: {
  size?: string;
  color?: string;
  cursor?: string;
  title?: string;
}) => {
  return (
    <Tooltip title={title}>
      <LockOutlinedIcon sx={{ cursor, height: size, width: size, color }} />
    </Tooltip>
  );
};

/** Countries list */
// export const sortedCountries = [...countries]?.sort((a, b) => {
//   const labelA = a.label.toLowerCase();
//   const labelB = b.label.toLowerCase();
//   if (labelA < labelB) {
//     return -1;
//   }
//   if (labelA > labelB) {
//     return 1;
//   }
//   return 0;
// });

export const sortedCountries = sortByKey(countries, 'label'); // A–Z
export const sortedCountriesDesc = sortByKey(countries, 'label', true, 'desc'); // Z–A

/** Sort by kay */
function sortByKey<T>(
  array: readonly T[],
  key: keyof T,
  caseInsensitive: boolean = true,
  order: 'asc' | 'desc' = 'asc'
): T[] {
  return [...array].sort((a, b) => {
    const valA = a[key];
    const valB = b[key];

    const aValue = caseInsensitive && typeof valA === 'string' ? valA.toLowerCase() : valA;
    const bValue = caseInsensitive && typeof valB === 'string' ? valB.toLowerCase() : valB;

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

/** Truncate Text */
export const truncateText = (text: any, maxLength: number) => {
  if (text && text.length > maxLength) {
    return `${text.slice(0, maxLength)}...`;
  } else {
    return text;
  }
};

/** Truncate Text with tooltip */
export const truncateTextWithTooltip = (text: string, maxLength: number) => {
  if (text && text.length > maxLength) {
    return (
      <Tooltip title={text}>
        <span>{`${text.slice(0, maxLength)}...`}</span>
      </Tooltip>
    );
  } else {
    return text;
  }
};

/** Display name */
export const getDisplayName = (each: any) => {
  const firstName: any = each?.displayName.split(' ')[0];
  const lastName: any = each?.displayName.split(' ').slice(1).join(' ');
  return `${firstName} ${lastName}`;
  // return firstName
  //   ?.charAt(0)
  //   ?.toUpperCase()
  //   ?.concat(firstName?.slice(1)?.toLowerCase())
  //   ?.concat(
  //     ' ',
  //     lastName?.charAt(0)?.toUpperCase()?.concat(lastName?.slice(1)?.toLowerCase())
  //   );
};

/** Get Border Color */
export const getBorderColor = (levelColor: any, taskActive?: boolean) => {
  if (!taskActive) {
    return '#ABB5BE'; // Gray color when task is not active
  }

  if (levelColor === 'gold' || levelColor === 'green') {
    return '#11895E'; // Color for gold or green levels (If you need green color take it '#11895E')
  }

  return '#ABB5BE'; // Default gray color
};

/** Get Trophy Badge */
export const getTrophyBadge = (hexColor: any, taskActive?: boolean) => {
  if (!taskActive) {
    return Trophybadge2; // Return Trophybadge2 if task is not active
  }

  switch (hexColor) {
    case 'gold':
      return Trophybadge; // Gold color Trophybadge
    case 'green':
      return ThrophyBadgeGreen; // Green color ThrophyBadgeGreen
    default:
      return Trophybadge2; // Default gray color Trophybadge2
  }
};

/** Goal Progressing color */
export const getProgressingColor = (
  value: number,
  average?: number,
  format: 'hex' = 'hex' // Always return hex color by default
): string => {
  const colors = {
    success: '#11895E', // Green
    warning: '#E99940', // Yellow
    error: '#DF6438' // Red
  };

  // Determine the result based on `average`
  const result: 'success' | 'warning' | 'error' =
    average !== undefined
      ? value > average
        ? 'success'
        : value === average
        ? 'warning'
        : 'error'
      : value >= 66
      ? 'success'
      : value >= 33
      ? 'warning'
      : 'error';

  // Always return hex color
  return colors[result];
};

/** Redirect to community user profile */
export const useRedirectUserCommunityProfile = () => {
  const navigate = useNavigate();
  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const commList = useAppSelector((state) => state.getCommunityMembers.data);

  const redirectUserCommunityProfile = (userId: string) => {
    const member = communityMembers.find((item) => item.userId === userId);
    const modifiedMember = { ...member, id: _.get(member, 'id.coId', '') };

    if (member) {
      navigate('/app/communitymember', {
        state: { mem: modifiedMember, allCommunityMembersList: commList }
      });
    }
  };

  return redirectUserCommunityProfile;
};

/** Status Color */
export const getStatusColor = (status: string) => {
  switch (status) {
    case 'to-do':
      return '#0071A9'; // TO Do color (blue / info.main)
    case 'inprogress':
      return '#E18C2D'; // In progress color (orange)
    case 'completed':
      return '#11895E'; // Completed color (green)
    default:
      return '#ABB5BE'; // Default to disabled color (secondary.light)
  }
};

/** Status text */
export const getStatusText = (status: string | undefined) => {
  switch (status) {
    case 'to-do':
      return 'Active';
    case 'inprogress':
      return 'Active';
    default:
      return status || 'Active';
  }
};
